import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth.js";
import CategoryNav from "./CategoryNav.jsx";

const HamburgerMenu = ({ user, logout }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className="relative sm:hidden">
      <button
        type="button"
        aria-label="Open menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex h-8 w-8 flex-col items-center justify-center gap-1.5 rounded-md text-white/70 hover:text-white"
      >
        <span className={`block h-0.5 w-5 bg-current transition-all duration-200 ${open ? "translate-y-2 rotate-45" : ""}`} />
        <span className={`block h-0.5 w-5 bg-current transition-all duration-200 ${open ? "opacity-0" : ""}`} />
        <span className={`block h-0.5 w-5 bg-current transition-all duration-200 ${open ? "-translate-y-2 -rotate-45" : ""}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full z-[60] mt-2 w-44 rounded-2xl border border-white/10 bg-slate-900 py-2 shadow-xl">
          <Link
            to="/leaderboard"
            onClick={() => setOpen(false)}
            className="block px-4 py-2 text-sm font-semibold text-white/70 hover:text-white"
          >
            Leaderboard
          </Link>
          {user ? (
            <button
              type="button"
              onClick={() => { setOpen(false); logout(); }}
              className="block w-full px-4 py-2 text-left text-sm font-semibold text-white/70 hover:text-white"
            >
              Sign out
            </button>
          ) : (
            <>
              <Link
                to="/login"
                onClick={() => setOpen(false)}
                className="block px-4 py-2 text-sm font-semibold text-white/70 hover:text-white"
              >
                Sign in
              </Link>
              <Link
                to="/register"
                onClick={() => setOpen(false)}
                className="block px-4 py-2 text-sm font-semibold text-white/70 hover:text-white"
              >
                Sign up
              </Link>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const Layout = () => {
  const location = useLocation();
  const [availableCategories, setAvailableCategories] = useState([]);
  const { user, logout } = useAuth();

  const selectedCategory = useMemo(() => {
    if (location.pathname === "/") {
      return "all";
    }
    if (location.pathname.startsWith("/category/")) {
      const [, categorySlug = "all"] = location.pathname.split("/").filter(Boolean);
      return categorySlug || "all";
    }
    return "all";
  }, [location.pathname]);

  const showCategoryNav = useMemo(() => {
    if (!availableCategories.length) {
      return false;
    }
    return location.pathname === "/" || location.pathname.startsWith("/category/");
  }, [location.pathname, availableCategories.length]);

  return (
    <div className="flex min-h-screen flex-col bg-slate-950 text-slate-50">
      <header className="border-b border-white/10 bg-slate-950/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
          <Link to="/" className="text-xl font-semibold tracking-tight">
            QuizFreak
          </Link>
          <div className="flex items-center gap-3 text-sm">
            {user ? (
              <>
                {/* Username + points — always visible */}
                <Link
                  to="/profile"
                  className="text-white/70 transition hover:text-white"
                >
                  {user.username}
                </Link>
                <span className="text-white/40">·</span>
                <span className="text-white/60 font-medium">{user.total_points} pts</span>

                {/* Desktop-only: leaderboard + sign out */}
                <Link
                  to="/leaderboard"
                  className="hidden text-sm font-semibold text-white/60 transition hover:text-white sm:inline"
                >
                  Leaderboard
                </Link>
                <button
                  type="button"
                  onClick={logout}
                  className="hidden rounded-full border border-white/20 px-4 py-1.5 text-xs font-semibold text-white/70 transition hover:border-white/40 hover:text-white sm:inline-flex"
                >
                  Sign out
                </button>

                {/* Mobile hamburger */}
                <HamburgerMenu user={user} logout={logout} />
              </>
            ) : (
              <>
                {/* Desktop-only: leaderboard */}
                <Link
                  to="/leaderboard"
                  className="hidden text-sm font-semibold text-white/60 transition hover:text-white sm:inline"
                >
                  Leaderboard
                </Link>

                {/* Desktop-only: sign in + sign up */}
                <Link
                  to="/login"
                  className="hidden text-sm font-semibold text-white/70 transition hover:text-white sm:inline"
                >
                  Sign in
                </Link>
                <Link
                  to="/register"
                  className="hidden rounded-full bg-brand-glow px-4 py-1.5 text-xs font-semibold text-white transition hover:opacity-90 sm:inline-flex"
                >
                  Sign up
                </Link>

                {/* Mobile hamburger */}
                <HamburgerMenu user={null} logout={logout} />
              </>
            )}
          </div>
        </div>
        {showCategoryNav && (
          <CategoryNav
            categories={availableCategories}
            selectedCategory={selectedCategory}
          />
        )}
      </header>
      <main className="mx-auto flex w-full max-w-6xl flex-1 px-4 py-8">
        <div className="w-full">
          <Outlet
            context={{
              setAvailableCategories,
            }}
          />
        </div>
      </main>
      <footer className="mt-auto border-t border-white/10 bg-slate-950/90">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-center gap-2 px-4 py-6 text-sm text-white/60 sm:flex-row">
          <span>&copy; {new Date().getFullYear()} QuizFreak</span>
          <div className="flex flex-wrap items-center gap-4 text-white/70">
            <a
              href="https://www.quizfreak.com/privacy"
              target="_blank"
              rel="noreferrer"
              className="transition hover:text-white"
            >
              Privacy Policy
            </a>
            <a
              href="https://www.quizfreak.com/contactus"
              target="_blank"
              rel="noreferrer"
              className="transition hover:text-white"
            >
              Contact Us
            </a>
            <a
              href="https://www.quizfreak.com/aboutus"
              target="_blank"
              rel="noreferrer"
              className="transition hover:text-white"
            >
              About Us
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
